import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Minus, Maximize } from "lucide-react";
import MD from "../data/mdMap.json";
import useMapView from "../hooks/useMapView";

const BUTTON_ZOOM_IN = 1.4;
const BUTTON_ZOOM_OUT = 0.7;
// Only auto-show every city label once the user zooms in deeper than this. At the
// default zoom (~3.6) only the primary pin labels itself — keeps the cluster readable.
const LABEL_ZOOM_THRESHOLD = 5.5;
const ZOOM_BUTTON_CLASSES =
  "w-9 h-9 rounded-xl bg-white shadow-md border border-slate-200 inline-flex items-center justify-center text-[#1A1D21] hover:bg-[#EDEFF2] transition";

function pinRadius(isHighlight, isHover, isPrimary, k, compact) {
  // In compact mode the SVG is rendered much smaller, so we need bigger logical pins
  // to keep them readable at the same pixel size. The /k compensation already adjusts
  // for the zoom factor, so we just bump the base.
  const scale = compact ? 1.55 : 1;
  let base = 6.5 * scale;
  if (isHighlight) {
    base = 9 * scale;
  } else if (isHover) {
    base = 8.5 * scale;
  } else if (isPrimary) {
    base = 8 * scale;
  }
  return base / k;
}

function pinFill(isHighlight) {
  if (isHighlight) {
    return "#B71420";
  }
  return "#E31D2B";
}

function CountyShapes({ k }) {
  return MD.counties.map((county, index) => (
    <path
      key={`${county.name}-${index}`}
      d={county.d}
      fill="#FAFCFF"
      stroke="#8FB4D4"
      strokeWidth={0.9 / k}
      strokeLinejoin="round"
    />
  ));
}

// Builds a "stadium" (capsule) outline = the convex hull of two equal-radius
// circles: a semicircle cap at each hub joined by straight tangent edges. This
// makes the two service radii read as ONE connected coverage area instead of
// two separate circles. (Sweep flag 1 bulges each cap outward — verified.)
function stadiumPath(h1, h2, r) {
  const dx = h2.cx - h1.cx;
  const dy = h2.cy - h1.cy;
  const len = Math.hypot(dx, dy) || 1;
  const px = -dy / len; // unit perpendicular to the hub axis
  const py = dx / len;
  const A = [h1.cx + px * r, h1.cy + py * r];
  const B = [h1.cx - px * r, h1.cy - py * r];
  const C = [h2.cx - px * r, h2.cy - py * r];
  const D = [h2.cx + px * r, h2.cy + py * r];
  return `M ${A[0]} ${A[1]} A ${r} ${r} 0 0 1 ${B[0]} ${B[1]} L ${C[0]} ${C[1]} A ${r} ${r} 0 0 1 ${D[0]} ${D[1]} Z`;
}

function ServiceRadius({ k }) {
  const { hubs, r } = MD.radius;
  if (!hubs || hubs.length < 2) return null;
  const d = stadiumPath(hubs[0], hubs[1], r);
  return (
    <>
      <path
        d={d}
        fill="#54616E"
        fillOpacity="0.10"
        stroke="#54616E"
        strokeWidth={1.8 / k}
        strokeDasharray={`${7 / k} ${5 / k}`}
        strokeLinejoin="round"
      />
      <path d={d} fill="none" stroke="#54616E" strokeOpacity="0.22" strokeWidth={4 / k} strokeLinejoin="round" />
      {hubs.map((h) => (
        <circle key={`hub-${h.cx}-${h.cy}`} cx={h.cx} cy={h.cy} r={3.5 / k} fill="#54616E" fillOpacity="0.6" />
      ))}
    </>
  );
}

function CityPin({ city, k, isHighlight, isHover, showLabel, compact, onHover, onLeave, onClick }) {
  const r = pinRadius(isHighlight, isHover, city.primary, k, compact);
  // Bump label font for compact maps so the small sidebar map is still readable.
  const labelFont = (compact ? 18 : 12.5) / k;
  return (
    <g
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={onClick}
      style={{ cursor: "pointer" }}
      data-testid={`map-city-${city.slug}`}
    >
      {(city.primary || isHighlight) && <circle className="abc-map-pulse" cx={city.x} cy={city.y} r={(compact ? 18 : 12) / k} fill="#E31D2B" />}
      <circle cx={city.x} cy={city.y} r={r + 5 / k} fill="transparent" />
      <circle
        cx={city.x}
        cy={city.y}
        r={r}
        fill={pinFill(isHighlight)}
        stroke="#FFFFFF"
        strokeWidth={(compact ? 2.5 : 2) / k}
        style={{ transition: "r 150ms ease" }}
      />
      {showLabel && (
        <text
          x={city.x}
          y={city.y - (r + 7 / k)}
          textAnchor="middle"
          fontSize={labelFont}
          fontWeight="700"
          fill="#1A1D21"
          stroke="#FFFFFF"
          strokeWidth={(compact ? 4.5 : 3.5) / k}
          paintOrder="stroke"
          style={{ fontFamily: "inherit", pointerEvents: "none" }}
        >
          {city.city}
        </text>
      )}
    </g>
  );
}

function ZoomControls({ onZoomIn, onZoomOut, onReset }) {
  return (
    <div className="absolute top-3 right-3 flex flex-col gap-1.5">
      <button onClick={onZoomIn} className={ZOOM_BUTTON_CLASSES} aria-label="Zoom in" data-testid="map-zoom-in">
        <Plus className="w-4 h-4" />
      </button>
      <button onClick={onZoomOut} className={ZOOM_BUTTON_CLASSES} aria-label="Zoom out" data-testid="map-zoom-out">
        <Minus className="w-4 h-4" />
      </button>
      <button onClick={onReset} className={ZOOM_BUTTON_CLASSES} aria-label="Reset view" data-testid="map-zoom-reset">
        <Maximize className="w-4 h-4" />
      </button>
    </div>
  );
}

function MapOverlays() {
  return (
    <>
      <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur rounded-xl shadow-md border border-slate-200 px-3.5 py-2.5" data-testid="map-legend">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-[#1A1D21]">
          <span className="w-3 h-3 rounded-full bg-[#E31D2B] ring-2 ring-white shadow flex-shrink-0" /> Cities we serve
        </div>
      </div>
      <div className="absolute bottom-3 right-3 text-[10px] sm:text-[11px] text-slate-600 bg-white/85 backdrop-blur px-2.5 py-1.5 rounded-full border border-slate-200">
        <span className="hidden sm:inline">Scroll to zoom · Drag to pan</span>
        <span className="sm:hidden">Pinch to zoom · Drag to pan</span>
      </div>
    </>
  );
}

export default function ServiceAreaMap({ highlightSlug = null, compact = false }) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);
  const { svgRef, view, pointerHandlers, zoomAtCenter, resetView, wasDragged } = useMapView(highlightSlug);
  const { k, tx, ty } = view;

  const handleCityClick = (slug) => {
    if (!wasDragged()) navigate(`/service-areas/${slug}`);
  };

  const isLabelVisible = (city) =>
    k >= LABEL_ZOOM_THRESHOLD || hovered === city.slug || city.primary || city.slug === highlightSlug;

  return (
    <div
      className="relative mx-auto w-full max-w-xl md:max-w-2xl lg:max-w-3xl rounded-3xl overflow-hidden border border-slate-200 bg-[#CFE7FB] shadow-sm select-none"
      data-testid="service-area-map"
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${MD.width} ${MD.height}`}
        className="w-full h-auto block cursor-grab active:cursor-grabbing"
        style={{ touchAction: "none" }}
        onPointerDown={pointerHandlers.onPointerDown}
        onPointerMove={pointerHandlers.onPointerMove}
        onPointerUp={pointerHandlers.onPointerUp}
        onPointerCancel={pointerHandlers.onPointerCancel}
        onMouseLeave={() => setHovered(null)}
        role="img"
        aria-label="Interactive map of Maryland showing Drain Sights service area cities and coverage radius"
      >
        <g transform={`translate(${tx} ${ty}) scale(${k})`} data-testid="map-canvas">
          <CountyShapes k={k} />
          <ServiceRadius k={k} />
          {MD.cities.map((city) => (
            <CityPin
              key={city.slug}
              city={city}
              k={k}
              isHighlight={city.slug === highlightSlug}
              isHover={hovered === city.slug}
              showLabel={isLabelVisible(city)}
              compact={compact}
              onHover={() => setHovered(city.slug)}
              onLeave={() => setHovered(null)}
              onClick={() => handleCityClick(city.slug)}
            />
          ))}
        </g>
      </svg>

      <ZoomControls
        onZoomIn={() => zoomAtCenter(BUTTON_ZOOM_IN)}
        onZoomOut={() => zoomAtCenter(BUTTON_ZOOM_OUT)}
        onReset={resetView}
      />
      {!compact && <MapOverlays />}
    </div>
  );
}
